import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";

export interface PaymentMethod {
  id: string;
  gym_id: string;
  name: string;
  is_active: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

export const usePaymentMethods = (gymId: string | null) => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotifications();

  const { data: methods, isLoading } = useQuery({
    queryKey: ["payment-methods", gymId],
    queryFn: async () => {
      if (!gymId) return [];
      const { data, error } = await supabase
        .from("payment_methods")
        .select("*")
        .eq("gym_id", gymId)
        .eq("is_active", true)
        .order("position", { ascending: true });
      if (error) throw error;
      return data as PaymentMethod[];
    },
    enabled: !!gymId,
  });

  const createMethod = useMutation({
    mutationFn: async (data: { gym_id: string; name: string }) => {
      const { data: result, error } = await supabase
        .from("payment_methods")
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-methods", gymId] });
      success("Método creado", { description: "El método de pago se creó correctamente" });
    },
    onError: (e) => {
      console.error(e);
      showError("No se pudo crear el método");
    },
  });

  const updateMethod = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; is_active?: boolean } }) => {
      const { data: result, error } = await supabase
        .from("payment_methods")
        .update(data)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-methods", gymId] });
      success("Método actualizado");
    },
    onError: (e) => {
      console.error(e);
      showError("No se pudo actualizar el método");
    },
  });

  const deleteMethod = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("payment_methods").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-methods", gymId] });
      success("Método eliminado");
    },
    onError: (e) => {
      console.error(e);
      showError("No se pudo eliminar el método");
    },
  });

  return {
    methods: methods || [],
    isLoading,
    createMethod,
    updateMethod,
    deleteMethod,
  };
};
